import { useFetch } from '@chipinside/fetcher'
import { MarkdownView } from '@chipinside/markdown'
import { Translate } from '@chipinside/translate'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { isEmpty, isNil } from 'lodash'

import Iframe from '#/components/HelpPages/iframe'
import { getLinksList } from '#/components/HelpPages/utils'
import { Icon } from '#/components/Icon'
import LinearLoader from '#/components/Loader/Linear'
import { endpoint } from '#/store/services/endpoints'
import { ResolvedPage } from '#/store/services/Pages/PagesService'
import { isBusinessEnvironment } from '#/utils'

type HelpRequest = {
  name: string
  name_text: string
  group: 'farm' | 'system'
  help: {
    description: string
    video_link: string | null
    links: Array<Record<string, string | null>>
    short_description?: string
    title?: string
  }
}

export function HelpContent({ page }: { page: Partial<ResolvedPage> }) {
  const { data, isLoading } = useFetch<HelpRequest>({
    controller: endpoint.database.help_pages.help({ help: page?.id }),
  })

  const help = data?.help
  const crumbs = data?.name_text
  const links = getLinksList(help?.links)

  return (
    <Box sx={{ mb: 4 }}>
      <Header />
      <PageTitle crumbs={crumbs} />

      <Divider sx={{ my: 2 }} />

      <LinearLoader loading={isLoading} />

      {help && (
        <Stack sx={{ gap: 2 }}>
          <Description help={help} />
          <MarkdownView>{help.description}</MarkdownView>
          <ExtraContent help={help} links={links} />
        </Stack>
      )}
    </Box>
  )
}

function Header() {
  return (
    <Typography color="warning" sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 2 }}>
      <Icon size="large" name="circle-question-mark" />
      {Translate({ messageKey: 'more' })}
    </Typography>
  )
}

function PageTitle({ crumbs }: { crumbs?: string }) {
  if (!crumbs) return null

  const [title] = crumbs.split('/').reverse()

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h5">{title}</Typography>
      <Typography variant="subtitle1" sx={{ color: 'grey.600', mb: 1 }}>
        {crumbs}
      </Typography>
    </Box>
  )
}

function Description({ help }: { help: HelpRequest['help'] }) {
  if (!isBusinessEnvironment()) return null

  const items = [
    { label: Translate({ messageKey: 'title' }), value: help?.title },
    { label: Translate({ messageKey: 'short_description' }), value: help?.short_description },
  ].filter(item => !isNil(item.value))

  if (isEmpty(items)) return null

  return (
    <Box sx={{ mb: 1 }}>
      {items.map(item => (
        <Typography sx={{ color: 'grey.600' }}>
          {item.label}:
          <Typography color="textPrimary" component="span" sx={{ ml: 0.5 }}>
            {item.value}
          </Typography>
        </Typography>
      ))}
    </Box>
  )
}

function ExtraContent({ help, links }: { help: HelpRequest['help']; links: React.ReactNode[] }) {
  if (!help.video_link && isEmpty(links)) return null

  return (
    <Stack sx={{ gap: 1.5 }}>
      <Typography variant="subtitle1" sx={{ color: 'grey.600', mt: 1.5 }}>
        {Translate({ messageKey: 'more_information' })}:
      </Typography>

      {help.video_link && <Iframe url={help.video_link} />}

      {!isEmpty(links) && (
        <>
          <Typography variant="subtitle1" sx={{ color: 'grey.600', mt: 2 }}>
            {Translate({ messageKey: 'useful_links' })}:
          </Typography>
          <Stack sx={{ gap: 1.5 }}>{links}</Stack>
        </>
      )}
    </Stack>
  )
}
